import useAsync from 'react-use/lib/useAsync';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Box, Typography } from '@material-ui/core';
import type { EntityScorecard } from '@internal/plugin-scorecards-common';
import { scorecardsApiRef } from '../api/ScorecardsApi';
import { RuleResultsTable } from './RuleResultsTable';
import { isLowCoverage, LevelChip, ScoreBar } from './ScoreBar';

/** `evaluated <time>`, or a plain notice when the backend sent no usable time. */
function evaluatedText(at: string): string {
  const time = Date.parse(at);
  return Number.isNaN(time)
    ? 'evaluation time unknown'
    : `evaluated ${new Date(time).toLocaleString()}`;
}

/** One scorecard on one card: its score and its rules. */
function ScorecardSection({
  scorecard,
  entityRef,
  several,
}: {
  scorecard: EntityScorecard;
  entityRef: string;
  /** Several scorecards on the page: say which template each belongs to. */
  several: boolean;
}) {
  return (
    <InfoCard
      title={several ? `Scorecard: ${scorecard.templateName}` : 'Scorecard'}
    >
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        style={{ gap: 24 }}
      >
        <Box minWidth={200}>
          {scorecard.score === null ? (
            <Typography>No rule could be scored yet.</Typography>
          ) : (
            <ScoreBar score={scorecard.score} coverage={scorecard.coverage} />
          )}
        </Box>
        <LevelChip level={scorecard.level} coverage={scorecard.coverage} />
        <Typography variant="body2" color="textSecondary">
          Template {scorecard.templateName} (version {scorecard.templateVersion}
          ), {evaluatedText(scorecard.evaluatedAt)}
        </Typography>
      </Box>
      {scorecard.score !== null && scorecard.coverage <= 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            No data is available for these rules: it has not been collected yet,
            or the component has no mapping for the source a rule uses.
          </Typography>
        </Box>
      )}
      {scorecard.score !== null &&
        scorecard.coverage > 0 &&
        isLowCoverage(scorecard.coverage) && (
          <Box mt={2}>
            {/* The ScoreBar caption already says "low confidence (N%)". */}
            <Typography variant="body2" color="textSecondary">
              Not every rule had data when this was scored: it may not have been
              collected yet, or the component has no mapping for the source a
              rule uses.
            </Typography>
          </Box>
        )}
      <Box mt={3}>
        <Typography variant="subtitle2" gutterBottom>
          Rules
        </Typography>
        <RuleResultsTable
          rules={scorecard.rules}
          entityRef={entityRef}
          idPrefix={scorecard.templateId}
          label={
            several ? `Rule results: ${scorecard.templateName}` : 'Rule results'
          }
        />
      </Box>
    </InfoCard>
  );
}

/** The Scorecard tab on a Component page: one section per matching template. */
export function EntityScorecardContent() {
  const { entity } = useEntity();
  const api = useApi(scorecardsApiRef);
  const entityRef = stringifyEntityRef(entity).toLowerCase();
  // useAsync drops the result of a superseded call, so a slow response for a
  // previous entity never overwrites the current one.
  const { value, loading, error } = useAsync(
    () => api.getEntityScorecards(entityRef),
    [api, entityRef],
  );

  if (loading) {
    return <Progress />;
  }
  if (error) {
    return <ResponseErrorPanel error={error} />;
  }
  if (!value || value.length === 0) {
    // The backend answers 404 for "not evaluated yet", "not scorable" and "no
    // template matches", so the three cannot be told apart: say so neutrally.
    return (
      <InfoCard title="Scorecard">
        <Typography>
          This component is not scored yet. It may not have been evaluated yet,
          no scorecard template may match it, or it may not map to any source:
          add a <code>github.com/project-slug</code>,{' '}
          <code>dev.azure.com/project-repo</code> or{' '}
          <code>sonarqube.org/project-key</code> annotation (or a GitHub or
          Azure DevOps <code>backstage.io/source-location</code>) to its catalog
          entry.
        </Typography>
      </InfoCard>
    );
  }

  return (
    <>
      {value.map((scorecard, index) => (
        <Box key={scorecard.templateId} mt={index === 0 ? 0 : 3}>
          <ScorecardSection
            scorecard={scorecard}
            entityRef={entityRef}
            several={value.length > 1}
          />
        </Box>
      ))}
    </>
  );
}
